import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Languages, ChefHat, Loader2, Lock, User, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { apiError } from "@/lib/api";
import { StaggerRoot, StaggerItem, MotionButton } from "@/components/motion";
import { fadeUp, useMotionPrefs, withMotionPrefs } from "@/lib/motion";

const DEMO_USERS = [
  { username: "owner", labelDari: "مالک", labelPashto: "مالک" },
  { username: "manager", labelDari: "مدیر", labelPashto: "مدیر" },
  { username: "cashier", labelDari: "حسابدار", labelPashto: "حسابدار" },
  { username: "waiter", labelDari: "گارسون", labelPashto: "ګارسون" },
  { username: "kitchen", labelDari: "آشپزخانه", labelPashto: "پخلنځی" },
  { username: "accountant", labelDari: "حسابدار", labelPashto: "حسابدار" },
];

const DEMO_LOGIN_ENABLED = process.env.REACT_APP_ENABLE_DEMO_LOGIN === "true";

const HOME_BY_ROLE = {
  owner: "/dashboard",
  manager: "/dashboard",
  accountant: "/dashboard",
  cashier: "/cashier",
  waiter: "/new-order",
  kitchen: "/kitchen",
};

export default function Login() {
  const { user, login, loading } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const { isReduced } = useMotionPrefs();
  const heroVariants = withMotionPrefs(fadeUp, isReduced);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        {lang === "pashto" ? "په بار کولو کې..." : "در حال بارگذاری..."}
      </div>
    );
  }

  if (user) return <Navigate to={HOME_BY_ROLE[user.role] || "/dashboard"} replace />;

  const submit = async (e, demoUser) => {
    e?.preventDefault?.();
    const u = demoUser?.username || username;
    const p = demoUser ? "123456" : password;
    if (!u || !p) {
      toast.error(lang === "pashto" ? "ټول ساحې ډک کړئ" : "همه فیلدها را پر کنید");
      return;
    }
    setSubmitting(true);
    try {
      const logged = await login(u, p);
      setLoginSuccess(true);
      toast.success(t("welcome") + "، " + logged.name);
      await new Promise((r) => setTimeout(r, isReduced ? 0 : 220));
      navigate(HOME_BY_ROLE[logged.role] || "/dashboard");
    } catch (err) {
      const msg = apiError(err);
      toast.error(err?.response?.status === 401 ? msg || t("invalidCreds") : msg, {
        id: "login-error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F7F8FA]" dir="rtl">
      <motion.div
        className="hidden lg:block w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(7,26,51,0.55), rgba(17,24,39,0.65)), url(/theme.png)",
        }}
        variants={heroVariants}
        initial="hidden"
        animate="show"
      >
        <div className="absolute inset-0 flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur grid place-items-center">
              <ChefHat className="w-6 h-6" />
            </div>
            <div>
              <div className="font-heading text-xl font-bold heading-plain-white">{t("appName")}</div>
              <div className="text-sm opacity-80">Restaurant Plus POS</div>
            </div>
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold leading-tight font-heading mb-3 heading-plain-white">
              {lang === "pashto"
                ? "یو پلتفورم — هر څه چې ستاسو ریسټورانټ ته اړتیا لري"
                : "یک سیستم برای کنترل کامل رستورانت شما"}
            </h1>
            <p className="text-white/80 max-w-md text-sm">
              {lang === "pashto"
                ? "د سفارش، پخلنځي، دخل، لګښت او ګټې مدیریت په یوه ځای کې."
                : "مدیریت سفارش، آشپزخانه، دخل، مصارف، قرضداران و گزارش‌های دقیق در یک سیستم."}
            </p>
          </div>
        </div>
      </motion.div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-10">
        <StaggerRoot className="w-full max-w-md">
          <StaggerItem index={0}>
            <div className="flex items-center justify-between mb-8">
              <div className="lg:hidden flex items-center gap-2">
                <div className="w-10 h-10 rounded-2xl bg-[#071A33] grid place-items-center text-white">
                  <ChefHat className="w-5 h-5" />
                </div>
                <div className="font-bold font-heading text-gray-900">{t("appName")}</div>
              </div>
              <button
                data-testid="login-lang"
                onClick={() => setLang(lang === "dari" ? "pashto" : "dari")}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-sm text-gray-700"
              >
                <Languages className="w-4 h-4" />
                {lang === "dari" ? "پښتو" : "دری"}
              </button>
            </div>
          </StaggerItem>

          <StaggerItem index={1}>
            <h2 className="text-2xl font-bold text-gray-900 font-heading mb-1">
              {t("login")}
            </h2>
            <p className="text-sm text-gray-500 mb-6">{t("tagline")}</p>
          </StaggerItem>

          <StaggerItem index={2}>
            <form onSubmit={submit} className="space-y-4 relative">
              <div>
                <label className="text-sm text-gray-600 mb-1.5 block">{t("username")}</label>
                <div className="relative">
                  <User className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    data-testid="login-username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t("username")}
                    className="w-full h-12 pr-10 pl-4 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#071A33] focus:ring-2 focus:ring-[#071A33]/20 text-gray-900"
                    autoComplete="username"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1.5 block">{t("password")}</label>
                <div className="relative group">
                  <Lock className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    data-testid="login-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••"
                    className="w-full h-12 pr-10 pl-12 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#071A33] focus:ring-2 focus:ring-[#071A33]/20 text-gray-900"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    data-testid="login-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? (lang === "pashto" ? "پټنوم پټ کړئ" : "مخفی کردن رمز") : (lang === "pashto" ? "پټنوم وښایاست" : "نمایش رمز")}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-lg grid place-items-center text-gray-400 hover:text-[#071A33] hover:bg-[#071A33]/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#071A33]/30 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-[18px] h-[18px]" strokeWidth={2} />
                    ) : (
                      <Eye className="w-[18px] h-[18px]" strokeWidth={2} />
                    )}
                  </button>
                </div>
              </div>
              <MotionButton
                data-testid="login-submit"
                type="submit"
                disabled={submitting}
                success={loginSuccess}
                className="w-full h-12 rounded-xl bg-[#071A33] hover:bg-[#7F1020] text-white font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {t("loginButton")}
              </MotionButton>
            </form>
          </StaggerItem>

          {DEMO_LOGIN_ENABLED && (
          <StaggerItem index={3}>
          <div className="mt-7">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-px bg-gray-200 flex-1" />
              <span className="text-xs text-gray-500">Demo Mode</span>
              <div className="h-px bg-gray-200 flex-1" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              {DEMO_USERS.map((u) => (
                <button
                  key={u.username}
                  data-testid={`demo-${u.username}`}
                  onClick={(e) => submit(e, u)}
                  disabled={submitting}
                  className="px-3 py-2.5 rounded-xl border border-gray-200 hover:border-[#071A33] hover:bg-[#071A33]/5 text-xs font-medium text-gray-700 transition-all"
                >
                  <div className="font-bold">{lang === "pashto" ? u.labelPashto : u.labelDari}</div>
                  <div className="text-[10px] text-gray-400 font-mono">{u.username}</div>
                </button>
              ))}
            </div>
            <div className="text-center mt-4 text-xs text-gray-400">
              {lang === "pashto" ? "پټنوم د ټولو لپاره" : "رمز برای همه"}: <span className="font-mono font-bold">123456</span>
            </div>
          </div>
          </StaggerItem>
          )}
        </StaggerRoot>
      </div>
    </div>
  );
}
